import React from 'react';
import styled, { css } from 'styled-components';
import Icon from './Icon';
import Loading from '../../components/Loading/Loading';
import Typography from './Typography';

type TextInputVariant = 'Grid' | 'Date';
type TextInputSize = 'Small' | 'Medium';
type TextInputState = 'Disabled' | 'Error';
export type TextInputType =
  | 'text'
  | 'number'
  | 'password'
  | 'date'
  | 'checkbox'
  | 'email';

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  testId?: string;
  label?: string;
  placeholder?: string;
  inputSize?: TextInputSize;
  state?: TextInputState;
  inputVariant?: TextInputVariant;
  type?: TextInputType;
  errorMessage?: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  ['data-testid']?: string;
  icon?: Kingpin.Icon;
  autoFocus?: boolean;
  width?: string;
  maxLength?: number;
  isLoading?: boolean;
  isTransparentBorders?: boolean;
  forwardRef?: React.MutableRefObject<HTMLInputElement | null>;
  isAutocompleteDisabled?: boolean;
  /**
   * @deprecated Use standartized styles defined in TextInputVariant.
   */
  style?: React.CSSProperties | undefined;
}

const HEIGHT_MEDIUM = 36;
const HEIGHT_SMALL = 32;

const PlaceholderCss = css<{ height: number }>`
  &::placeholder {
    color: var(--color-text-muted);

    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: ${(props) => props.height}px;
  }
`;

const GridInputCss = css`
  max-height: 22px;
  min-height: 22px;
  text-align: right;
`;

const DateInputCss = css`
  padding: 0;
  text-align: center;
`;

const MediumCss = css<{
  inputSize: TextInputSize;
  inputState?: TextInputState;
  hasIcon: boolean;
}>`
  padding: ${(props) => (props.hasIcon ? '6px 12px 6px 28px' : '6px 12px')};
`;

const SmallCss = css<{
  inputSize: TextInputSize;
  inputState?: TextInputState;
  hasIcon: boolean;
}>`
  padding: ${(props) => (props.hasIcon ? '3px 12px 3px 28px' : '3px 12px')};
`;

const DefaultStateCss = css<{ isTransparentBorders?: boolean }>`
  border: 1px solid
    ${(props) =>
      props.isTransparentBorders ? 'transparent' : 'var(--color-gainsboro)'};
  background: var(--color-bg-primary);
  transition:
    box-shadow 225ms ease,
    border-color 150ms ease,
    background-color 150ms ease;

  box-shadow: var(--shadow-sm);
`;

const DisabledStateCss = css`
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);

  box-shadow: var(--shadow-sm);

  &:hover {
    cursor: not-allowed;
  }
`;

const FocusedStateCss = css<{
  inputState?: TextInputState;
}>`
  &:focus {
    border: 1px solid
      ${(props) =>
        props.inputState === 'Error'
          ? 'var(--color-status-error)'
          : 'var(--color-info-v2)'};
    background: var(--color-bg-primary);
  }

  &:focus-within {
    outline: unset;
  }
`;

const ErrorStateCss = css`
  border: 1px solid var(--color-status-error);
  background: var(--color-bg-primary);

  box-shadow: var(--shadow-sm);
`;

const Input = styled.input<{
  inputSize: TextInputSize;
  inputState?: TextInputState;
  inputVariant?: TextInputVariant;
  hasIcon: boolean;
  width?: string;
  height: number;
  isTransparentBorders?: boolean;
}>`
  display: flex;
  height: ${(props) => props.height}px;
  min-height: ${(props) => props.height}px;
  width: ${(props) => props.width || '100%'};
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  color: var(--color-text-primary);
  font-family: Inter;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: ${(props) => props.height}px;

  ${(props) => props.hasIcon && 'padding-left: 16px;'}

  ${PlaceholderCss};
  // Size
  ${({ inputSize }) => inputSize === 'Small' && SmallCss}
  ${({ inputSize }) => inputSize === 'Medium' && MediumCss}
  
  // State
  ${({ inputState }) => {
    switch (inputState) {
      case 'Disabled':
        return DisabledStateCss;
      case 'Error':
        return ErrorStateCss;
      default:
        return DefaultStateCss;
    }
  }}
  
  

  ${(props) => props.inputState !== 'Disabled' && FocusedStateCss}

  // Variant
  ${({ inputVariant }) => {
    switch (inputVariant) {
      case 'Grid':
        return GridInputCss;
      case 'Date':
        return DateInputCss;
      default:
        return '';
    }
  }}
`;

const ErrorSpan = styled.span`
  color: var(--color-status-error);

  font-family: Inter;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const ErrorDiv = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    height: 16px;
    width: 16px;
    margin-right: 8px;
  }
`;

const ICON_HEIGHT = 16;
const InputIcon = styled.div`
  z-index: 1;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 8px;
  margin: auto 0;
  height: ${ICON_HEIGHT}px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;

  svg {
    height: ${ICON_HEIGHT}px;
    width: ${ICON_HEIGHT}px;
  }
`;

const LoadingIcon = styled.div`
  z-index: 1;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 8px;
  margin: auto 0;
  height: ${ICON_HEIGHT}px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;

  svg {
    height: ${ICON_HEIGHT}px;
    width: ${ICON_HEIGHT}px;
  }
`;

const getHeight = (size: TextInputSize | undefined) => {
  switch (size) {
    case 'Medium':
      return HEIGHT_MEDIUM;
    case 'Small':
    default:
      return HEIGHT_SMALL;
  }
};

export const ErrorMessage = ({ message }: { message: string }) => {
  return (
    <ErrorDiv>
      <Icon icon="cross" color={'var(--color-status-error)'} />
      <ErrorSpan>{message}</ErrorSpan>
    </ErrorDiv>
  );
};

const TextInput = (props: TextInputProps) => {
  const height = getHeight(props.inputSize);

  const Component = (
    <Input
      {...props}
      inputSize={props.inputSize || 'Small'}
      inputState={props.state}
      inputVariant={props.inputVariant}
      type={props.type || 'text'}
      hasIcon={!!props.icon}
      readOnly={props.state === 'Disabled'}
      disabled={props.state === 'Disabled'}
      autoComplete={props.isAutocompleteDisabled ? 'new-password' : 'undefined'}
      height={height}
      ref={props.forwardRef}
    />
  );

  const { label, errorMessage, icon, isLoading } = props;

  // Allow for inline rendering.
  //
  // The branch decision deliberately ignores `errorMessage`: it renders as a
  // sibling of the input here rather than forcing the wrapped branch below.
  // Switching branches when `errorMessage` toggles would move the <input> to a
  // different position in the tree, so React remounts it and focus is lost
  // mid-typing. Keeping the input's position stable across error toggles keeps
  // focus intact.
  if (!label && !icon && !isLoading) {
    return (
      <>
        {Component}
        {errorMessage && <ErrorMessage message={errorMessage} />}
      </>
    );
  }

  // More complex states cannot be used inline
  return (
    <div style={{ width: '100%' }}>
      {label && (
        <div style={{ marginBottom: 4 }}>
          <Typography.Body type={'Label'}>{label}</Typography.Body>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        {icon && (
          <InputIcon>
            <Icon icon={icon} color={'var(--color-text-muted)'} />
          </InputIcon>
        )}
        {Component}
        {isLoading && (
          <LoadingIcon>
            <Loading isTiny />
          </LoadingIcon>
        )}
      </div>
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  );
};

export default TextInput;
